/**
 * Leitura da proposta (payload → entrada da conta) e as opções da
 * Apresentação. O estudo completo é montado em estudo.ts; a conta do
 * comparativo está em comparativo.ts e as taxas, em indicadoresBcb.ts.
 *
 * A comparação é sempre do mesmo segmento da proposta: móvel contra
 * financiamento de automóvel, imóvel contra financiamento imobiliário.
 */
import type { Indicadores } from './indicadoresBcb.ts';
import {
  montarComparativo,
  type Comparativo,
  type EntradaComparativo,
  type Segmento,
  type Taxas,
} from './comparativo.ts';
import type { PayloadProposta } from './tipos.ts';

/**
 * Ágio suposto na venda da carta contemplada, em % do crédito.
 * Não há índice oficial — é o que o mercado paga numa negociação típica. O PDF
 * sempre mostra ao lado o ágio de EMPATE, que não depende desta suposição.
 */
export const AGIO_PADRAO_PCT = 10;

/** O que o vendedor escolhe na caixinha antes de gerar. */
export type OpcoesComparativo = {
  segmento: Segmento;
  /** Juros do financiamento em % a.a.; null = média de mercado do BCB. */
  jurosAa: number | null;
  mesContemplacao: number;
  agioPct: number;
  /** CDI e reajuste em % a.a.; ausente/null = valor do BCB. */
  cdiAa?: number | null;
  reajusteAa?: number | null;
};

/** Juros de mercado do segmento — nunca o do outro segmento. */
export const jurosDeMercado = (ind: Indicadores, segmento: Segmento) =>
  segmento === 'imovel' ? ind.jurosImovelAa : ind.jurosVeiculoAa;

export const taxasDasOpcoes = (ind: Indicadores, o: OpcoesComparativo): Taxas => ({
  jurosFinanciamentoAa: o.jurosAa ?? jurosDeMercado(ind, o.segmento).valor,
  cdiAa: o.cdiAa ?? ind.cdiAa.valor,
  reajusteAa: o.reajusteAa ?? (o.segmento === 'imovel' ? ind.incc12m.valor : ind.ipca12m.valor),
  agioPct: o.agioPct,
});

export const NOME_SEGMENTO: Record<Segmento, string> = { imovel: 'imóvel', veiculo: 'automóvel' };

const num = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

export const segmentoDaModalidade = (modalidade: unknown): Segmento | null => {
  const m = String(modalidade ?? '').toLowerCase();
  if (m.startsWith('imov') || m.startsWith('imóv') || m === 'i') return 'imovel';
  if (m.startsWith('auto') || m.startsWith('veic') || m.startsWith('veíc') || m === 'm') return 'veiculo';
  return null;
};

/**
 * Payload da proposta → entrada da conta. Devolve null quando falta o básico
 * (crédito, prazo, taxa): sem isso não há comparação honesta a fazer.
 *
 * A parcela pré-contemplação é a `parcelaJunta` (já com flex e junção) menos o
 * seguro, que o flex não desconta — ver parcela-flex.ts.
 */
export const entradaDoPayload = (payload: PayloadProposta, segmento: Segmento): EntradaComparativo | null => {
  const credito = num(payload.valorCredito);
  const prazo = num(payload.prazo);
  const taxaAdminPct = num(payload.taxaAdmin);
  if (!credito || credito <= 0 || !prazo || prazo <= 1 || !taxaAdminPct || taxaAdminPct <= 0) return null;

  const cotas = parseInt(String(payload.juncaoCota ?? '1'), 10) || 1;
  const seguro = String(payload.seguro ?? '').toLowerCase() === 'sim' ? (num(payload.valorSeguro) ?? 0) * cotas : 0;
  const parcelaJunta = num(payload.parcelaJunta);
  const parcelaPre = parcelaJunta && parcelaJunta - seguro > 0
    ? parcelaJunta - seguro
    : (credito * (1 + taxaAdminPct / 100)) / prazo;

  return {
    credito,
    prazo,
    taxaAdminPct,
    parcelaPre,
    mesContemplacao: Math.min(Math.max(1, num(payload.mesesContemplacao) ?? 12), prazo),
    segmento,
  };
};

/**
 * Payload + opções → a conta pronta. null quando o payload não tem crédito,
 * prazo ou taxa. A caixinha usa isto para a prévia; o PDF, para o desenho.
 */
export const prepararComparativo = (
  payload: PayloadProposta,
  opcoes: OpcoesComparativo,
  ind: Indicadores,
): Comparativo | null => {
  const entrada = entradaDoPayload(payload, opcoes.segmento);
  if (!entrada) return null;
  return montarComparativo(
    { ...entrada, mesContemplacao: Math.min(Math.max(1, Math.round(opcoes.mesContemplacao)), entrada.prazo) },
    taxasDasOpcoes(ind, opcoes),
  );
};
